<template>
  <!-- Careful when alterning 'cf-carousel__item' class as it affects qr code rendering and scroll dots focus -->
  <div class="cf-carousel__item">
    <div class="cf-card cf-flex cf-flex--column cf-flex--gap-16">
      <!-- Credit Card Details -->
      <cf-credit-card-display
        v-if="isCreditCard"
        :payment-method="paymentMethod"
      />

      <cf-gift-card
        v-else
        type="details"
        :card-design="giftCardDesignBackground"
      />

      <div class="cf-grid cf-grid--2-grid-column-equal">
        <!-- Balance Details or Expiration -->
        <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center cf-flex--gap-12">
          <p class="cf-subtitle cf-subtitle--1">
            {{ content.label }}
          </p>

          <h1 class="cf-title cf-title--1">
            {{ content.subLabel }}
          </h1>
        </div>

        <!-- QR Code -->
        <cf-loading-wrapper
          :is-loading="isLoadingQrCode"
          :loader-type="'section'"
        >
          <div class="cf-qr-code-container">
            <vue-qrcode
              v-if="qrCode"
              class="cf-image cf-image--auto-w cf-image--border"
              :value="qrCode"
              :scale="2"
            />
          </div>
        </cf-loading-wrapper>
      </div>

      <p
        v-if="qrCode"
        :class="['cf-subtitle cf-subtitle--2 cf-text-align-center', { 'transparent': !qrCode }]"
      >
        Code will refresh in: <span class="cf-subtitle--heavy">{{ codeExpirationDisplayTime }}</span>
      </p>

      <!-- Manage/Delete Buttons -->
      <button
        class="cf-button cf-button--primary cf-button--lg cf-button--fill"
        type="button"
        @click="$openModal('managePaymentMethodModal', { paymentMethod })"
      >
        <span class="icon fa-cog" />

        Manage Card
      </button>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import moment from 'moment-timezone';

  import http from '@/services/http';

  import errorHandlers from '@/services/error-handlers';

  import CfCreditCardDisplay from '../cf-credit-card-display/index.vue';

  import CfGiftCard from '@/view/components/globals/cf-gift-card/index.vue';
  import CfLoadingWrapper from '@/view/components/globals/cf-loading-wrapper/index.vue';

  import dollars from '@/filters/dollars';


  export default {
    name: 'CfPaymentMethodToScan',

    components: {
      CfCreditCardDisplay,
      CfGiftCard,
      CfLoadingWrapper
    },

    props: {
      componentIdx: {
        type: Number,
        required: true
      },

      focusedPaymentMethodIdx: {
        type: Number,
        required: true
      },

      paymentMethod: {
        type: Object,
        required: true
      },

      tipAmount: {
        type: Number,
        default: 0
      },

      appliedOffer: {
        type: Object,
        default: () => {}
      },

      offerRedemptionCode: {
        type: String,
        default: ''
      },

      offerKeyToken: {
        type: String,
        default: null
      }
    },

    data() {
      return {
        shouldShowFullCardNumber: false,
        isLoadingQrCode: false,
        qrCode: '',
        codeExpirationInterval: null,
        secondsUntilCodeExpiration: 0,
        codeExpirationTimestamp: null,
        cachedStringifiedData: ''
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser']),

      giftCardDesignBackground() {
        return this.paymentMethod.design;
      },

      content() {
        const label = this.isCreditCard ? 'Expiration' : 'Balance';

        const subLabel = this.isCreditCard
          ? this.paymentMethod.expirationDisplay
          : dollars(this.paymentMethod.balance);

        return {
          label,
          subLabel
        };
      },

      isCreditCard() {
        return !!this.paymentMethod.creditCardId;
      },

      isCurrentFocusedPaymentMethod() {
        return this.componentIdx === this.focusedPaymentMethodIdx;
      },

      codeExpirationDisplayTime() {
        if (this.secondsUntilCodeExpiration === 0) {
          return '0:00';
        }

        const minutes = Math.floor(this.secondsUntilCodeExpiration / 60);
        const seconds = this.secondsUntilCodeExpiration - (minutes * 60);
        const secondsPadded = seconds < 10 ? `0${seconds}` : seconds;

        return `${minutes}:${secondsPadded}`;
      }
    },

    watch: {
      focusedPaymentMethodIdx: 'watchPaymentMethodChange',
      tipAmount: 'watchDataChange',
      offerRedemptionCode: 'watchDataChange',
      offerKeyToken: 'watchDataChange'
    },

    created() {
      if (this.isCurrentFocusedPaymentMethod) {
        this.getQrCode();
      }
    },

    unmounted() {
      this.stopCodeExpirationTimer();
    },

    methods: {
      watchPaymentMethodChange() {
        /**
         * Get stringified data for current qr code, tip amount and offer redemption code to
         * compare against cached stringified data to only request a new qr code
         * when either tip amount or redemption code changes.
         */
        const newStringifiedData = this.getStringifiedData();

        const isMatch = newStringifiedData === this.cachedStringifiedData;

        if (this.isCurrentFocusedPaymentMethod && (!this.cachedStringifiedData || !isMatch)) {
          this.getQrCode();
        }
      },

      watchDataChange() {
        if (this.isCurrentFocusedPaymentMethod) {
          this.getQrCode();
        }
      },

      getStringifiedData() {
        return JSON.stringify({ qrCode: this.qrCode, tipAmount: this.tipAmount, offerRedemptionCode: this.offerRedemptionCode });
      },

      async getQrCode() {
        this.isLoadingQrCode = true;

        this.stopCodeExpirationTimer();

        const currentUserAccountId = this.currentUser.accountId;

        try {
          const requestBody = this.formatQrCodeRequestBody();

          const { data } = await http.post(`/cardfree-api/v1/account/${currentUserAccountId}/single-scan-check-in-tokens`, requestBody);

          this.qrCode = data.checkinToken;

          this.codeExpirationTimestamp = data.expirationDate;

          /**
          * Set cached stringified data for current qr code, tip amount and offer redemption code
          * to be later compared when swiping between payment methods.
          */

          this.cachedStringifiedData = this.getStringifiedData();

          this.startCodeExpirationTimer();
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isLoadingQrCode = false;
        }
      },

      formatQrCodeRequestBody() {
        const paymentToken = this.isCreditCard ? this.paymentMethod.creditCardId : this.paymentMethod.cardId;

        const savedPaymentMethodSource = this.isCreditCard ? 1 : 2;

        const body = {
          paymentToken,
          savedPaymentMethodSource
        };

        if (this.offerRedemptionCode) Object.assign(body, { offerRedemptionCode: this.offerRedemptionCode });
        if (this.offerKeyToken) Object.assign(body, { offerKeyToken: this.offerKeyToken });
        if (this.tipAmount) Object.assign(body, { tipInCents: Math.round(this.tipAmount * 100) });

        return body;
      },

      startCodeExpirationTimer() {
        const now = moment();
        const expiration = moment(this.codeExpirationTimestamp);
        this.secondsUntilCodeExpiration = Math.ceil(expiration.diff(now, 'seconds', true));
        this.codeExpirationInterval = setInterval(this.codeExpirationTimerTick, 1000);
      },

      stopCodeExpirationTimer() {
        clearInterval(this.codeExpirationInterval);
      },

      codeExpirationTimerTick() {
        this.secondsUntilCodeExpiration--;

        if (this.secondsUntilCodeExpiration === 0) {
          this.getQrCode();
        }
      }
    }
  };
</script>

<style lang="sass" scoped>
.qr-code-container
  display: flex
  align-items: center
  justify-content: center
  min-height: 140px
</style>
